import React, { useLayoutEffect, useRef, useState } from "react";

/**
 * Cadre d'affichage d'un document "PDF" (rendu HTML imprimé via window.print()).
 *
 * La page est rendue à sa largeur A4 naturelle (210mm) puis, si la largeur
 * disponible est insuffisante (mobile), tout le document est rétréci
 * proportionnellement via transform: scale() au lieu de casser la mise en page
 * (le contenu interne est exprimé en mm et ne suit pas un simple width:100%).
 *
 * Conserve l'id="pdfWithBorder" et la classe "editable-pdfContent" attendus par
 * les handlers d'impression (document.getElementById("pdfWithBorder")).
 *
 * Le transform inline est neutralisé à l'impression par une règle @media print
 * dans App.css (#pdfWithBorder { transform: none !important }).
 */
export default function PdfPageFrame({ className = "", style = {}, children, ...rest }) {
  const wrapperRef = useRef(null);
  const pageRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [wrapperHeight, setWrapperHeight] = useState(undefined);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const page = pageRef.current;
    if (!wrapper || !page) return;

    let rafId = null;
    let lastScale = null;
    let lastHeight = null;

    const update = () => {
      rafId = null;
      // offsetWidth/offsetHeight = dimensions de layout, non affectées par le transform
      const pageWidth = page.offsetWidth;
      const available = wrapper.clientWidth;
      // Ignorer quand le conteneur est masqué (ex. #root caché pendant l'impression)
      // pour ne pas calculer un scale(0).
      if (!pageWidth || !available) return;
      const nextScale = Math.min(1, available / pageWidth);
      const nextHeight = nextScale < 1 ? page.offsetHeight * nextScale : undefined;
      // Éviter les setState redondants (sinon re-render inutile et boucle ResizeObserver).
      if (nextScale !== lastScale) {
        lastScale = nextScale;
        setScale(nextScale);
      }
      if (nextHeight !== lastHeight) {
        lastHeight = nextHeight;
        setWrapperHeight(nextHeight);
      }
    };

    // On diffère la mesure dans un requestAnimationFrame : cela casse la boucle de
    // notification du ResizeObserver ("loop completed with undelivered notifications").
    const schedule = () => {
      if (rafId == null) rafId = requestAnimationFrame(update);
    };

    schedule();

    const ro = new ResizeObserver(schedule);
    ro.observe(wrapper);
    ro.observe(page);
    return () => {
      ro.disconnect();
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="pdf-page-frame"
      style={{ width: "100%", height: wrapperHeight, overflow: "hidden" }}
    >
      <div
        ref={pageRef}
        className={`editable-pdfContent${className ? " " + className : ""}`}
        id="pdfWithBorder"
        style={{
          width: "210mm",
          minHeight: "280mm",
          position: "relative",
          backgroundColor: "white",
          margin: scale < 1 ? 0 : "auto",
          transform: scale < 1 ? `scale(${scale})` : undefined,
          transformOrigin: "top left",
          ...style,
        }}
        {...rest}
      >
        {children}
      </div>
    </div>
  );
}
